/**
 * Password policy shown live on the reset screen.
 * Mirrors the server-side rule set in api/app/Rules/StrongPassword.php — keep
 * the two in step or the UI will promise something the API rejects.
 */

export interface PasswordRule {
  id: string;
  label: string;
  test: (password: string, context: PasswordContext) => boolean;
}

export interface PasswordContext {
  /** Hashes of the user's recent passwords, if the API supplied them. */
  previouslyUsed?: string[];
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    label: 'At least 12 characters',
    test: (password) => password.length >= 12,
  },
  {
    id: 'case',
    label: 'At least one uppercase and lowercase letter',
    test: (password) => /[a-z]/.test(password) && /[A-Z]/.test(password),
  },
  {
    id: 'complexity',
    label: 'At least one number and one special character',
    test: (password) => /\d/.test(password) && /[^A-Za-z0-9]/.test(password),
  },
  {
    id: 'reuse',
    label: 'Has not been recently used',
    // The definitive check is server-side; this only flags an obviously empty value.
    test: (password) => password.length > 0,
  },
];

export type StrengthLevel = 'weak' | 'fair' | 'good' | 'strong';

export interface PasswordStrength {
  level: StrengthLevel;
  label: string;
  /** 0–4, drives the four-segment meter. */
  score: number;
  colorClass: string;
}

export function evaluatePassword(password: string, context: PasswordContext = {}): PasswordStrength {
  if (!password) {
    return { level: 'weak', label: 'Enter a password', score: 0, colorClass: 'bg-line' };
  }

  const passed = PASSWORD_RULES.filter((rule) => rule.test(password, context)).length;

  // Length beyond the minimum earns the top band; rules alone cap at "good".
  let score = passed;
  if (passed === PASSWORD_RULES.length && password.length >= 16) score = 4;
  else if (passed === PASSWORD_RULES.length) score = 3;

  const bands: Record<number, PasswordStrength> = {
    0: { level: 'weak', label: 'Weak', score: 0, colorClass: 'bg-danger' },
    1: { level: 'weak', label: 'Weak', score: 1, colorClass: 'bg-danger' },
    2: { level: 'fair', label: 'Fair', score: 2, colorClass: 'bg-gold-500' },
    3: { level: 'good', label: 'Good', score: 3, colorClass: 'bg-digital' },
    4: { level: 'strong', label: 'Strong', score: 4, colorClass: 'bg-signal' },
  };

  return bands[score] ?? bands[0]!;
}

/** All rules satisfied and the confirmation matches. */
export function canSubmitPassword(password: string, confirmation: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(password, {})) && password === confirmation;
}
